import { convertPayloadToArray, getRootDomain, useConfig } from "./utils.js";

export const VARIANT_FOR_FEE_PRODUCT = window.productDualSaleRushFeeVariantID
  ? window.productDualSaleRushFeeVariantID
  : window.defaultDualSaleRushFeeVariantID;
export const RUSH_FEE_OPTION = window.dualSaleRushOptionName;
const HIDDEN_PROPERTY_NAME = "_original_variant_id";
const PROMISED_BY_DATE_PROPERTY_NAME = "_PROMISED_BY_DATE";

export const eventNames = {
  ADD_ITEM: "cart:addItem",
  REMOVE_ITEM: "cart:removeItem",
  UPDATE_ITEM_QUANTITY: "cart:updateItemQuantity",
  RUSH_FEE_SYNC_COMPLETE: "cart:rushFeeSyncComplete",
  OPEN_CART: "cart:open",
  CART_DATA_UPDATED: "cart:dataUpdated",
};

/**
 * Validates a variant object.
 *
 * @param {{ id: number, quantity: number, properties?: Object }} variant - The product object to validate.
 * @returns {boolean} - True if the product object is valid, false otherwise.
 */
export const variantIsFormatted = ({ id, quantity, properties = {} }) =>
  typeof id === "number" &&
  !Number.isNaN(quantity) &&
  quantity > 0 &&
  typeof properties === "object";

/**
 * Maps an array of products to an object with key-value pairs of product key to quantity.
 *
 * @param {Array<Object>} products - An array of objects with a "key" property and a "quantity" property.
 * @returns {Object|false} - An object with key-value pairs of product key to quantity, or false if the input is not an array.
 * @throws {Error} - If any product object is missing a "key" property.
 */
export const mapProductsForUpdate = (products) => {
  if (!Array.isArray(products)) return false;

  return products.reduce((acc, i) => {
    if (!Object.prototype.hasOwnProperty.call(i, "key")) {
      throw Error("missing `key` name in property");
    } else {
      acc[i.key] = i.quantity;
      return acc;
    }
  }, {});
};

/**
 * Fetches a product's production_days metafield using Storefront API.
 *
 * @param {string} productHandle - The handle of the product to use in the graphQL query.
 * @returns {Promise<string|null>} - The production days as a string or null if not found.
 */
export const getProductionDays = async (productHandle) => {
  const { shop } = window.Shopify;
  const { storefrontApiAccessToken, storefrontApiVersion } = useConfig();
  const apiVersion = storefrontApiVersion;
  const accessToken = storefrontApiAccessToken;
  const url = `https://${shop}/api/${apiVersion}/graphql.json`;
  const query = `
          query {
            product(handle: "${productHandle}") {
              metafield(namespace: "custom", key: "production_days") {
                value
              }
            }
          }
        `;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": accessToken,
    },
    body: JSON.stringify({ query }),
  });

  if (!res.ok) {
    throw new Error(res.message || res.statusText || res.status);
  }

  const result = await res.json();

  if (result.errors) {
    const errorMessage = result.errors.map((error) => error.message).join(", ");
    throw new Error(
      `Error during getProductionDays from graphQL request: ${errorMessage}`,
    );
  }

  const productionDays = result?.data?.product?.metafield?.value;
  return productionDays;
};

/**
 * Calculates a promised by date by adding the production timeline in days to the current time.
 * The calculation is done to match cart--mini-cart.liquid by using the Unix timestamp then converting back to local.
 * @param {*} productionTimeline - The number of days to add to the current date.
 * @returns {string} - The promised by date formatted as 'MM-DD-YYYY'
 */
export const calculatePromisedByDate = (productionTimeline) => {
  // Current Unix timestamp in seconds
  const currentTimestamp = Math.floor(Date.now() / 1000);

  // Convert productionTimeline (in days) to seconds and add it to the current timestamp
  const promisedTimestamp =
    currentTimestamp + parseInt(productionTimeline, 10) * 24 * 60 * 60;

  // Convert the promised timestamp back to a date in the local timezone
  const promisedDate = new Date(promisedTimestamp * 1000);

  // Format the date as MM-DD-YYYY in the local timezone
  const month = (promisedDate.getMonth() + 1).toString().padStart(2, "0");
  const day = promisedDate.getDate().toString().padStart(2, "0");
  const year = promisedDate.getFullYear();

  return `${month}-${day}-${year}`;
};

/**
 * Audits the promised by dates for each MTO item in the cart.
 * Writes to each MTO item's properties _PROMISED_BY_DATE if it exists.
 * @param {*} cart
 * @returns
 */
export const addPromisedByDateProperty = async (cart) => {
  const { items } = cart;

  const updatedItems = await Promise.all(
    items.map(async (item) => {
      const itemIsMto = Boolean(item.properties?.["_MADE-TO-ORDER"]);
      if (!itemIsMto) return item;

      // Get production days based on item type
      let productionDays = await getProductionDays(item.handle);
      const lowerCasedProductTitle = item.product_title.toLowerCase();

      // Apply default delays if production days not set
      if (!productionDays && lowerCasedProductTitle.includes("matte satin")) {
        const defaultMatteSatinDelayDays = "63";
        productionDays =
          window.daysDelayedMatteSatin || defaultMatteSatinDelayDays;
      } else if (!productionDays) {
        const defaultStandardMtoDays = "56";
        productionDays = window.daysDelayedMto || defaultStandardMtoDays;
      }
      const productionDaysMinusOneWeek = parseInt(productionDays, 10) - 7;
      const promisedByDateLowerRange = calculatePromisedByDate(
        productionDaysMinusOneWeek,
      );
      const promisedByDateUpperRange = calculatePromisedByDate(productionDays);
      const promisedByDate = `${promisedByDateLowerRange} and ${promisedByDateUpperRange}`;

      // Update when there's a discrepancy between the real time calculation and the stored property
      if (
        item.properties[PROMISED_BY_DATE_PROPERTY_NAME] &&
        item.properties[PROMISED_BY_DATE_PROPERTY_NAME] !== promisedByDate
      ) {
        try {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: item.key,
              properties: {
                ...item.properties,
                _PROMISED_BY_DATE: promisedByDate,
              },
            }),
          });
          const data = await response.json();

          // Find the updated item to return
          const updatedItem = data.items.find(
            (newItem) => newItem.id === item.id,
          );
          if (updatedItem) {
            return updatedItem;
          }
        } catch (error) {
          console.error("Error updating cart:", error);
        }
      }
      // If we don't need to make an update return the original item
      return item;
    }),
  );
  return { ...cart, items: updatedItems };
};

/**
 * Fetches the current cart data from the server.
 * Dispatches a custom event to open the cart.
 *
 * @param {string} detail.action - The action passed from the event. An "add" action will dispatch a CART_SUCCESS event.
 * @returns {Promise<Object>} A promise that resolves to cart object data.
 */
export const getCart = () =>
  fetch("/cart.js")
    .then(async (res) => {
      let json = {};

      if (
        res.headers.get("content-type")?.includes("json") ||
        res.headers.get("content-type")?.includes("javascript")
      ) {
        json = await res.json();
      }

      if (!res.ok) {
        throw new Error(json.message || res.statusText || res.status);
      }

      const cart = await addPromisedByDateProperty(json);
      window.cart = cart;
      return cart;
    })
    .catch((error) => {
      throw new Error(`Fetch error during getCart: ${error.message}`);
    });
// Stores Ajax fetch cart call to window so we can toggle between Storefront and Ajax APIs
window.fetchAjaxCart = getCart;

/**
 * Adds an item to the cart.
 *
 * @param {*} payload - The payload to add to the cart. Can be a single item or an array of items.
 * @param {*} eventPayload.data.action - The action from the eventPayload, which is passed for the CART_DATA_UPDATED event later on.
 * @returns {Promise<Response>} - A promise that resolves to the response from the server, or rejects with an error.
 * @error {Error} - Logs an error to catch with Track JS if server returns an error response.
 */
export const addItemToCart = async (payload, eventPayload) => {
  try {
    const resp = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: convertPayloadToArray(payload),
      }),
    });

    if (!resp.ok) {
      const json = await resp.json();
      throw new Error(json.message || resp.statusText || resp.status);
    }

    window.dispatchEvent(
      new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE, {
        detail: eventPayload,
      }),
    );
    return resp;
  } catch (error) {
    return console.error(error);
  }
};

/**
 * Updates the quantity of a line item in the cart.
 *
 * @param {*} payload - The payload containing the line item to update and its new quantity.
 * @returns {Promise<Response>} - A promise that resolves to the response from the server, or rejects with an error.
 * @error {Error} - Logs an error to catch with Track JS if server returns an error response.
 */
export const updateLineItemQuantity = async (payload) => {
  try {
    const resp = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updates: mapProductsForUpdate(convertPayloadToArray(payload)),
      }),
    });

    if (!resp.ok) {
      const json = await resp.json();
      throw new Error(json.message || resp.statusText || resp.status);
    }

    window.dispatchEvent(new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE));
    return resp;
  } catch (error) {
    return console.error(error);
  }
};

/**
 * Changes the line item's quantity using it's line item key.
 *
 * @params {string} lineItemKey - The key associated with the line item.
 * @params {number} newLineItemQuantity - The new quantity we want to update the line item with.
 * @returns {boolean} - True is successful, otherwise false.
 */
const changeLineItemQuantity = async (lineItemKey, newLineItemQuantity) => {
  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        quantity: newLineItemQuantity,
        id: String(lineItemKey),
      }),
    });
    if (!response.ok) {
      throw new Error("Network response not ok");
    }
    return true;
  } catch (error) {
    console.error("Error at checkout updating line item quantity:", error);
    return false;
  }
};

/**
 * Formats variant data for rush fee calculation.
 *
 * @param {object} item - The variant item data.
 * @param {string} variantForRushFee - The variant ID for rush fee calculation.
 * @returns {object} - The formatted product data for rush fee calculation.
 */
export const formatRushFeeProductData = (item, variantForRushFee) => {
  const customProperties = {};

  if (Object.prototype.hasOwnProperty.call(item, "product_title")) {
    // eslint-disable-next-line no-underscore-dangle
    customProperties._productName = item.product_title;

    /**
     * Split the product title by hyphen, which separates the style and color.
     * The first index represents the dress style to use as a line item e.g., "Laurie Chiffon Dress"
     */
    // eslint-disable-next-line no-underscore-dangle
    customProperties._productNameWithoutColor = item.product_title
      .split("-")[0]
      .trim();
  }

  if (Object.prototype.hasOwnProperty.call(item, "variant_options")) {
    // eslint-disable-next-line no-underscore-dangle

    /**
     * Create an array of filtered variant options where we accept items that aren't the RUSH_FEE_OPTION.
     * Build a string from that array to use as a line item e.g., "M / DUSTY BLUE"
     */
    const variantOptions = item.variant_options;
    const filteredVariantOptions = variantOptions.filter(
      (option) => option.trim() !== RUSH_FEE_OPTION,
    );
    const variantNameWithoutArrival = filteredVariantOptions.join(" / ");
    // eslint-disable-next-line no-underscore-dangle
    customProperties._variantNameWithoutArrival =
      variantNameWithoutArrival.trim();
  }

  if (Object.prototype.hasOwnProperty.call(item, "variant_title")) {
    // eslint-disable-next-line no-underscore-dangle
    customProperties._variantName = item.variant_title;
  }

  if (variantForRushFee) {
    customProperties[HIDDEN_PROPERTY_NAME] = item.id;
  }

  return {
    id: variantForRushFee,
    quantity: 1,
    properties: {
      ...customProperties,
    },
  };
};

/**
 * Finds a rush fee product in the cart by its name and ID.
 *
 * @param {object} cart - The cart object containing items.
 * @param {number} variantToMatch - The variant ID to match.
 * @returns {(object|undefined)} - The rush fee product item if found, otherwise undefined.
 */
export const findRushFeeProductByNameAndId = (cart, variantToMatch) => {
  if (!cart || !cart.items) return null;
  return cart.items.find(
    (item) =>
      item.properties &&
      item.properties[HIDDEN_PROPERTY_NAME] === Number(variantToMatch),
  );
};

/**
 * Updates the quantity of a rush fee product in the cart.
 *
 * @param {object} item - The item object containing variant ID, cart, and quantity.
 * @param {string} variantForRushFee - The variant ID for rush fee calculation.
 * @returns {boolean|object} - True if the quantity is updated successfully, false if not, or the result of updateLineItemQuantity.
 */
export const updateFeeProductQuantity = (item, variantForRushFee) => {
  if (!variantForRushFee) {
    /**
     * If we got here, which would be odd, we should open the cart
     * as we cannot make any changes to the cart without necessary args.
     */
    window.dispatchEvent(new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE));
    return false;
  }

  const { variantId, cart, quantity } = item;

  const foundRushFeeProduct = findRushFeeProductByNameAndId(cart, variantId);

  if (!foundRushFeeProduct) {
    /**
     * If we got here, which would be odd, we should open the cart
     * as we cannot make any changes to the cart without necessary args.
     */
    window.dispatchEvent(new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE));
    return false;
  }

  return updateLineItemQuantity({
    key: foundRushFeeProduct.key,
    quantity,
  });
};

/**
 * Adds a rush fee product to the cart if eligible products are found.
 *
 * @param {array} lineItems - The array of line items in the cart.
 * @param {string} variantForRushFee - The variant ID for rush fee calculation.
 * @param {object} payloadFromEvents - Payload to pass into RUSH_FEE_SYNC_COMPLETE which includes addToCartResponse, and klaviyoProduct
 * @returns {(boolean|object)} - True if the rush fee product is added successfully, false if not, or the result of dispatching the RUSH_FEE_SYNC_COMPLETE event.
 */
export const addFeeProduct = (
  lineItems,
  variantForRushFee,
  payloadFromEvents,
) => {
  if (!variantForRushFee || lineItems.length === 0) {
    /**
     * If we got here, which would be odd, we should open the cart
     * as we cannot make any changes to the cart without necessary args.
     */
    window.dispatchEvent(
      new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE, {
        detail: payloadFromEvents,
      }),
    );
    return false;
  }

  const hasEligibleProductForRushFee = lineItems.find((item) =>
    item.variant_options.includes(RUSH_FEE_OPTION),
  );

  if (hasEligibleProductForRushFee) {
    const formattedPayload = formatRushFeeProductData(
      hasEligibleProductForRushFee,
      variantForRushFee,
    );

    /**
     * This adds the rush fee item to the cart.
     * We pass in the payload from the previous event as well for RUSH_FEE_SYNC_COMPLETE later on.
     */
    return addItemToCart(formattedPayload, payloadFromEvents);
  }

  window.dispatchEvent(
    new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE, {
      detail: payloadFromEvents,
    }),
  );
  return false;
};

/**
 * Helper function to find the item quantity within a cart object.
 *
 * @returns {number} quantity - The quantity of the specific line item passed.
 */
export const getLineItemQuantity = (cart, lineItemId) => {
  let quantity = 0;
  cart.items.forEach((item) => {
    if (parseInt(item.id, 10) === parseInt(lineItemId, 10)) {
      quantity = item.quantity;
    }
  });
  return quantity;
};

/**
 * Audits the rush fees in cart.
 * If the quantities do not match the quantities of their associated products, update them.
 *
 * @params {rushFeeVariantId} - The variant id of the rush fee to audit.
 * @returns {void}
 */
export const auditRushFees = async (cart) => {
  let cartChanged = false;
  try {
    const promises = cart.items.map(async (item) => {
      if (item.product_title.includes("Rush Fee")) {
        /**
         * Find associated product and update the quantity if there's a diff
         *
         * Use the key for the rush fee in case multiple fees exist for different products
         */
        const rushFeeLineItemKey = item.key;
        const rushFeeLineItemQuantity = item.quantity;
        const associatedProductLineItemId =
          item.properties[HIDDEN_PROPERTY_NAME];
        /**
         * There are two scenarios the customer may encounter:
         *
         * 1) Customer orders X amount but now only Y amount are available -> we swap in the Y quantity to Rush Fee
         * 2) Customer orders X amount but now 0 are available -> we still swap in the Y quantity to Rush Fee
         */
        const associatedProductLineItemQuantity = getLineItemQuantity(
          cart,
          associatedProductLineItemId,
        );
        if (rushFeeLineItemQuantity !== associatedProductLineItemQuantity) {
          const success = changeLineItemQuantity(
            rushFeeLineItemKey,
            associatedProductLineItemQuantity,
          );
          if (success) cartChanged = true;
        }
      }
    });

    await Promise.all(promises);

    /**
     * Reload the page if updates were successful.
     * Use date time search param in URL to cache bust.
     */
    if (cartChanged) {
      const url = new URL(window.location.href);
      url.searchParams.set("_", new Date().getTime());
      window.location.replace(url.toString());
    }
  } catch (error) {
    console.error("Error auditing items in the cart:", error);
  }
};

/**
 * Extracts a Cart ID from a raw Cart token string, removing the key param
 * @param {string} token raw Cart token
 * @returns {string | null} cart ID if found, null otherwise
 */
export const extractCartIdFromToken = (token) => {
  const regex = /([a-zA-Z0-9]+)(?=\?)/;

  const match = token.match(regex);

  if (match) {
    return match[0];
  }

  return null;
};

const CART_META_COOKIE_KEY = "coreCart";
const SUITS_CART_META_COOKIE_KEY = "suitsCart";

/**
 * Writes Cart metadata to a cookie to be shared between BG Core and Suits
 * @param {string} cartId
 * @param {object} data metadata to be written to the cookie
 */
export const writeCartMetaToCookie = (cartId, data, expiresInDays = 30) => {
  const rawCookieValue = {
    cartId,
    updated: new Date().toUTCString(),
    ...data,
  };

  const cookieVal = JSON.stringify(rawCookieValue);
  const keyVal = `${CART_META_COOKIE_KEY}=${cookieVal.trim()};`;

  const expiry = new Date();
  expiry.setDate(expiry.getDate() + expiresInDays);

  const expires = `expires=${expiry.toUTCString()};`;
  const path = "path=/;";
  const domain = `Domain=${getRootDomain()};`;
  const cookie = keyVal + expires + path + domain;

  document.cookie = cookie;
};

/**
 * Retrieves and parses a cart meta cookie from the document
 * @param {string} cookieKey name of the cookie key to extract
 * @returns {CartMetaCookie}
 */
const getCartMetaCookie = (cookieKey) => {
  const regex = new RegExp(String.raw`${cookieKey}=(\{.*?\})`);

  const match = document.cookie.match(regex);

  if (match) {
    const cartMeta = match[1];
    try {
      const parsed = JSON.parse(cartMeta);
      return parsed;
    } catch (e) {
      console.error(`Unable to parse ${cookieKey} cookie`, e);
    }
  }

  return null;
};

/**
 * Retrieves the Core Cart meta cookie
 * @returns {CartMetaCookie}
 */
export const getCoreCartMetaCookie = () =>
  getCartMetaCookie(CART_META_COOKIE_KEY);

/**
 * Retrieves the Suits Cart meta cookie
 * @returns {CartMetaCookie}
 */
export const getSuitsCartMetaCookie = () =>
  getCartMetaCookie(SUITS_CART_META_COOKIE_KEY);

/**

/**
 * Clears the Core Cart meta cookie
 */
export const clearCartMetaCookie = () => {
  document.cookie = `${CART_META_COOKIE_KEY}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

export default {};
